import { alert } from 'actions/flash'
import { getPointsExperiences } from 'actions/pointsExperiences'
import {
  useGetGrantableRewardsQuery,
  useResolveOrderRatingMutation,
  useSendOrderRatingMessageMutation,
} from 'api/orderRatings'
import type { OrderRatingDetail } from 'api/orderRatings.types'
import { getErrorMessage } from 'api/utils'
import { GrantRewardPayload } from 'components/GrantRewardModal'
import { ResolveWithReplyPayload } from 'components/ResolveWithReplyModal'
import { SendMessagePayload } from 'components/SendMessageModal'
import useDispatch from 'hooks/useDispatch'
import { usePermissionReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectCurrentMerchantUser } from 'selectors/merchantUser'
import { selectExperience } from 'selectors/pointsExperience'
import type { RatingDetailActions } from './ratingDetailView'

const t = buildTranslate('order_ratings.rating_detail')

type UseRatingDetailActionsOpts = {
  canManageFeedback: boolean
  canManageRewardIssuance: boolean
  // The page's access gate — conditions the one-time points-experience fetch
  // the same way it conditions the rating query's `skip`, so a viewer about to
  // be bounced never fires the request.
  canViewDetails: boolean
}

// Everything the header's action row and the three modals need, flattened: the
// derived `headerActions` (RatingDetailActions) plus the modal-open state, the
// resolve/send mutation state, the grant catalog, the points gate, and the four
// handlers. The composer spreads these onto the ready view's `actions`/`modals`.
export type RatingDetailActionsBundle = {
  headerActions: RatingDetailActions
  // Resolve mutation state — shared by the grant and resolve modals.
  isResolving: boolean
  isResolveError: boolean
  resetResolve: () => void
  // Grant reward
  isGrantModalOpen: boolean
  onCloseGrant: () => void
  availableRewards: NonNullable<
    ReturnType<typeof useGetGrantableRewardsQuery>['data']
  >
  isFetchingRewards: boolean
  isRewardsError: boolean
  showPointsOption: boolean
  onGrantConfirm: (payload: GrantRewardPayload) => Promise<unknown>
  // Mark as resolved (with optional reply)
  isResolveModalOpen: boolean
  onCloseResolve: () => void
  operatorEmail: string | null
  onResolveConfirm: (payload: ResolveWithReplyPayload) => Promise<unknown>
  // Send message
  isSendMessageModalOpen: boolean
  onCloseSendMessage: () => void
  onSendMessage: (payload: SendMessagePayload) => Promise<unknown>
  isSendingMessage: boolean
  isSendMessageError: boolean
  resetSendMessage: () => void
  sendMessageErrorMessage: string
}

// Owns the ratings-detail scene's operator actions: the three modal-open
// toggles, the resolve and send-message mutations, the reset-on-rating-change
// effects, the permission-reason tooltips and derived disabled flags, the grant
// reward catalog, the points-experience gate, and the four handlers. Every hook
// is called unconditionally and the handlers guard a null rating, so the
// composer can call it ahead of its guards (rules of hooks).
export const useRatingDetailActions = (
  rating: OrderRatingDetail | null,
  ratingId: string,
  {
    canManageFeedback,
    canManageRewardIssuance,
    canViewDetails,
  }: UseRatingDetailActionsOpts
): RatingDetailActionsBundle => {
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false)
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false)
  const [isSendMessageModalOpen, setIsSendMessageModalOpen] = useState(false)

  // Drives both header actions (the grant modal calls the same trigger with a
  // reward payload). isResolveError renders inside the resolve and grant
  // dialogs; a success needs no local handling — the mutation's tag
  // invalidation refetches the rating, which comes back operator-resolved and
  // unmounts the whole row.
  const [
    resolveOrderRating,
    { isError: isResolveError, isLoading: isResolving, reset: resetResolve },
  ] = useResolveOrderRatingMutation()

  // Both controls are inert for a read-only viewer and while a resolve is in
  // flight (a second click would fire a duplicate write).
  // Split permission-missing from disabled: the BUTTON is inert for either
  // reason, but only a permission gap should surface a tooltip. Feeding the
  // combined flag to PermissionTooltip would explain an in-flight resolve as a
  // permissions problem.
  const isGrantPermissionMissing =
    !canManageFeedback || !canManageRewardIssuance
  const isResolveDisabled = !canManageFeedback || isResolving
  const isGrantDisabled = isGrantPermissionMissing || isResolving
  // Send has its OWN flag rather than borrowing isResolveDisabled: it shares the
  // same predicate today (write permission, and inert while a resolve is in
  // flight), but Send never mutates the rating, so the two are not the same
  // concept and shouldn't be coupled.
  const isSendMessageDisabled = !canManageFeedback || isResolving

  // Keyed to the PERMISSION alone, never `isResolveDisabled`/`isGrantDisabled`
  // — those fold in `isResolving`, so a permitted operator mid-resolve would
  // be handed a tooltip (and a role="note" aria-label) claiming they lack the
  // permission. Shared by both the resolve button and the grant-reward
  // button's Feedback branch — same subject, same args, one hook call (rules
  // of hooks: called unconditionally, order stable).
  const feedbackPermissionReason = usePermissionReason(
    'Feedback',
    !canManageFeedback,
    '(view and edit)'
  )
  // Grant reward can be blocked by either permission; the tooltip should name
  // whichever one is ACTUALLY missing, not just "Feedback" — checked against
  // the permission alone (not isGrantDisabled), so an in-flight resolve never
  // paints a misleading "missing permission" reason. Both hooks are called
  // unconditionally (rules of hooks) and the result picked afterwards.
  const grantRewardIssuanceReason = usePermissionReason(
    'RewardIssuance',
    !canManageRewardIssuance,
    '(view and edit)'
  )
  const grantReason = !canManageFeedback
    ? feedbackPermissionReason
    : grantRewardIssuanceReason

  // The mutation state is instance-scoped and survives navigation between
  // ratings, so clear any prior resolve error (and close the modal) when the
  // viewed rating changes — a failed resolve on one rating must not carry its
  // error onto the next. Keyed on ratingId only: RTK's `reset` changes identity
  // every time the mutation fires, so listing it here would re-run this effect
  // right after each grant/resolve and wipe the error and the open dialog.
  useEffect(() => {
    resetResolve()
    setIsGrantModalOpen(false)
    setIsResolveModalOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingId])

  // Send-only: emails the customer WITHOUT resolving the rating, so it owns its
  // own mutation instance entirely separate from resolveOrderRating above (same
  // reset-on-rating-change rationale).
  const [
    sendOrderRatingMessage,
    {
      isError: isSendMessageError,
      isLoading: isSendingMessage,
      error: sendMessageError,
      reset: resetSendMessage,
    },
  ] = useSendOrderRatingMessageMutation()

  useEffect(() => {
    resetSendMessage()
    setIsSendMessageModalOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [ratingId])

  // The grant modal's reward catalog, from the non-RAI
  // `order_ratings/available_rewards` route. NOT the RAI catalog: that route is
  // gated behind the `recovery-ai` flag, which is dark for non-RAI merchants, so
  // it 404s and leaves this dropdown empty for them (AI-1017). Skipped while the
  // modal is closed so the request only fires when an operator opens it.
  const {
    data: availableRewards = [],
    isFetching: isFetchingRewards,
    isError: isRewardsError,
  } = useGetGrantableRewardsQuery(undefined, { skip: !isGrantModalOpen })

  // Points can only be granted where a points experience exists — the same gate
  // the customer-profile grant modal uses. No experience → the modal renders
  // reward-only, with no radio group.
  const pointsExperience = useSelector(selectExperience)
  const dispatch = useDispatch()

  // Nothing else on this page loads the points experience, so the gate above
  // would read a permanently-empty store and hide the points path from every
  // merchant that has one. Fetch it once if it isn't there — the same guard
  // scenes/Users/View/Counters uses. Conditioned on the page's own guard, the
  // way the rating query's `skip` is: this hook sits above the redirects (rules
  // of hooks), and a viewer about to be bounced shouldn't fire a request.
  useEffect(() => {
    if (canViewDetails && !pointsExperience) {
      dispatch(getPointsExperiences())
    }
  }, [dispatch, pointsExperience, canViewDetails])

  // The LOGGED-IN OPERATOR (not the rating's customer): the resolve modal names
  // their address as where the customer's replies will land, because the BE sets
  // it as the Reply-To on the message it sends. Null until the current merchant
  // user is in the store, which hides that footer.
  const currentMerchantUser = useSelector(selectCurrentMerchantUser)
  const operatorEmail = currentMerchantUser?.email ?? null

  // The dismiss path runs through the resolve modal, which asks whether to also
  // send the customer a written reply. Resolving happens either way — the reply
  // is the only optional part.
  const handleResolveConfirm = (payload: ResolveWithReplyPayload) => {
    if (!rating) return Promise.resolve()
    return resolveOrderRating({ id: rating.id, ...payload }).unwrap()
  }

  // Mark as resolved opens the confirmation (ResolveWithReplyModal).
  const handleMarkResolved = () => {
    if (!rating) return
    setIsResolveModalOpen(true)
  }

  // The shared modal hands back the resolve body minus the id; this surface adds
  // the rating's. `.unwrap()` so a failure rejects and the modal stays open on
  // its error banner instead of closing over a write that didn't happen.
  const handleGrantConfirm = (payload: GrantRewardPayload) => {
    if (!rating) return Promise.resolve()
    return resolveOrderRating({ id: rating.id, ...payload }).unwrap()
  }

  // Send-only — `.unwrap()` so a failure rejects and the modal stays open on
  // its own error banner. On success a flash is the ONLY confirmation: sending
  // leaves the rating's state untouched, so the page looks identical afterward
  // and the operator could otherwise re-send, firing a duplicate customer email.
  const handleSendMessage = (payload: SendMessagePayload) => {
    if (!rating) return Promise.resolve()
    return sendOrderRatingMessage({ id: rating.id, ...payload })
      .unwrap()
      .then(() => {
        dispatch(
          alert({
            key: 'success',
            message: t('send_message.success'),
            timeout: 5,
          })
        )
      })
  }

  // Prefer the backend's specific 422 copy over the generic fallback, so
  // "please try again" never masks an actionable reason.
  const sendMessageErrorMessage = sendMessageError
    ? getErrorMessage(sendMessageError, t('send_message.error'))
    : t('send_message.error')

  const headerActions: RatingDetailActions = {
    canManageFeedback,
    isGrantPermissionMissing,
    grantReason,
    feedbackPermissionReason,
    isGrantDisabled,
    isResolveDisabled,
    isSendMessageDisabled,
    onGrant: () => setIsGrantModalOpen(true),
    onSendMessage: () => setIsSendMessageModalOpen(true),
    onMarkResolved: handleMarkResolved,
  }

  return {
    headerActions,
    isResolving,
    isResolveError,
    resetResolve,
    isGrantModalOpen,
    onCloseGrant: () => setIsGrantModalOpen(false),
    availableRewards,
    isFetchingRewards,
    isRewardsError,
    showPointsOption: !!pointsExperience,
    onGrantConfirm: handleGrantConfirm,
    isResolveModalOpen,
    onCloseResolve: () => setIsResolveModalOpen(false),
    operatorEmail,
    onResolveConfirm: handleResolveConfirm,
    isSendMessageModalOpen,
    onCloseSendMessage: () => setIsSendMessageModalOpen(false),
    onSendMessage: handleSendMessage,
    isSendingMessage,
    isSendMessageError,
    resetSendMessage,
    sendMessageErrorMessage,
  }
}
